import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Upload, 
  FileSpreadsheet, 
  Download, 
  AlertCircle, 
  CheckCircle2, 
  X,
  ArrowLeft,
  Loader2,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryUpload = () => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [successCount, setSuccessCount] = useState(0);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.csv')) {
      toast.error(isUSA ? 'Only CSV or Excel files are allowed' : 'Solo se permiten archivos CSV o Excel');
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setPreviewData(null);
    parseFile(selectedFile);
  };

  const parseFile = async (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setErrors([isUSA ? 'The file must have at least one header row and one data row' : 'El archivo debe tener al menos una fila de encabezados y una de datos']);
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const requiredHeaders = isUSA ? ['name', 'quantity'] : ['nombre', 'cantidad'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

      if (missingHeaders.length > 0) {
        setErrors([isUSA ? `Missing required columns: ${missingHeaders.join(', ')}` : `Faltan columnas requeridas: ${missingHeaders.join(', ')}`]);
        return;
      }

      const data = lines.slice(1).map((line, index) => {
        const values = line.split(',');
        const row = {};
        headers.forEach((header, i) => {
          row[header] = values[i]?.trim() || '';
        });
        row._rowNum = index + 2;
        return row;
      });

      setPreviewData({
        headers,
        rows: data.slice(0, 10),
        totalRows: data.length
      });
    };

    reader.onerror = () => {
      setErrors([isUSA ? 'Error reading the file' : 'Error al leer el archivo']);
    };

    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setErrors([]);
    setSuccessCount(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
      
      const response = await fetch(`${API_URL}/api/inventory/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || (isUSA ? 'Error uploading inventory' : 'Error al subir inventario'));
      }

      const result = await response.json();
      setSuccessCount(result.processed || 0);
      setUploadProgress(100);
      
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      }

      toast.success(isUSA ? `${result.processed} products processed` : `Se procesaron ${result.processed} productos`);
    } catch (error) {
      toast.error(error.message);
      setErrors([error.message]);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = isUSA 
      ? `name,quantity,sku,location,notes
White Cotton Fabric,500,FAB-COT-001,Warehouse A,Stock available
Navy Blue Jersey,250,JER-NAV-002,Warehouse B,Restock in 2 weeks
Black Interlock,1000,INT-BLK-003,Warehouse A,`
      : `nombre,cantidad,sku,ubicacion,notas
Tela Algodón Blanco,500,TEL-ALG-001,Almacén A,Stock disponible
Jersey Azul Marino,250,JER-AZU-002,Almacén B,Próxima reposición en 2 semanas
Interlock Negro,1000,INT-NEG-003,Almacén A,`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = isUSA ? 'inventory_template.csv' : 'plantilla_inventario.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/products')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isUSA ? 'Upload Inventory' : 'Subir Inventario'}</h1>
          <p className="text-slate-600">{isUSA ? 'Update your inventory from a CSV or Excel file' : 'Actualiza tu inventario desde un archivo CSV o Excel'}</p>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Instructions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              {isUSA ? 'File Format' : 'Formato del archivo'}
            </CardTitle>
            <CardDescription>
              {isUSA ? 'Your file must include the following columns' : 'Tu archivo debe incluir las siguientes columnas'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-slate-900 mb-2">{isUSA ? 'Required columns:' : 'Columnas requeridas:'}</h4>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200">{isUSA ? 'name' : 'nombre'}</Badge>
                    {isUSA ? 'Product name' : 'Nombre del producto'}
                  </li>
                  <li className="flex items-center gap-2">
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200">{isUSA ? 'quantity' : 'cantidad'}</Badge>
                    {isUSA ? 'Stock quantity' : 'Cantidad en stock'}
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 mb-2">{isUSA ? 'Optional columns:' : 'Columnas opcionales:'}</h4>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Badge variant="outline">sku</Badge>
                    {isUSA ? 'Product code' : 'Código del producto'}
                  </li>
                  <li className="flex items-center gap-2">
                    <Badge variant="outline">{isUSA ? 'location' : 'ubicacion'}</Badge>
                    {isUSA ? 'Warehouse location' : 'Ubicación en almacén'}
                  </li>
                  <li className="flex items-center gap-2">
                    <Badge variant="outline">{isUSA ? 'notes' : 'notas'}</Badge>
                    {isUSA ? 'Additional notes' : 'Notas adicionales'}
                  </li>
                </ul>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={downloadTemplate}
            >
              <Download className="w-4 h-4 mr-2" />
              {isUSA ? 'Download CSV template' : 'Descargar plantilla CSV'}
            </Button>
          </CardContent>
        </Card>

        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle>{isUSA ? 'Upload file' : 'Subir archivo'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                file ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => fileInputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
                  <div className="text-left">
                    <p className="font-medium text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setPreviewData(null);
                      setErrors([]);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-2">
                    {isUSA ? 'Drag your file here or click to select' : 'Arrastra tu archivo aquí o haz clic para seleccionar'}
                  </p>
                  <p className="text-sm text-slate-400">
                    {isUSA ? 'Accepted formats: CSV, Excel (.xlsx, .xls)' : 'Formatos aceptados: CSV, Excel (.xlsx, .xls)'}
                  </p>
                </>
              )}
            </div>

            {/* Preview */}
            {previewData && (
              <div className="mt-6">
                <h4 className="font-medium text-slate-900 mb-3">
                  {isUSA ? `Preview (${previewData.totalRows} records)` : `Vista previa (${previewData.totalRows} registros)`}
                </h4>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        {previewData.headers.map((header, i) => (
                          <th key={i} className="px-4 py-2 text-left font-medium text-slate-600">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows.map((row, i) => (
                        <tr key={i} className="border-t">
                          {previewData.headers.map((header, j) => (
                            <td key={j} className="px-4 py-2 text-slate-700">
                              {row[header] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {previewData.totalRows > 10 && (
                  <p className="text-sm text-slate-500 mt-2">
                    {isUSA ? `Showing 10 of ${previewData.totalRows} records` : `Mostrando 10 de ${previewData.totalRows} registros`}
                  </p>
                )}
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">{isUSA ? 'Errors found:' : 'Errores encontrados:'}</p>
                    <ul className="mt-2 space-y-1 text-sm text-red-600">
                      {errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Success message */}
            {successCount > 0 && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <p className="text-emerald-800">
                    {isUSA 
                      ? <><strong>{successCount}</strong> products updated successfully</>
                      : <>Se actualizaron <strong>{successCount}</strong> productos exitosamente</>}
                  </p>
                </div>
              </div>
            )}

            {/* Upload progress */}
            {uploading && (
              <div className="mt-4">
                <div className="flex items-center gap-3 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  <span className="text-sm text-slate-600">{isUSA ? 'Processing...' : 'Procesando...'}</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleUpload}
                disabled={!file || uploading || errors.length > 0}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isUSA ? 'Processing...' : 'Procesando...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {isUSA ? 'Upload inventory' : 'Subir inventario'}
                  </>
                )}
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/dashboard/products')}
              >
                {isUSA ? 'Cancel' : 'Cancelar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InventoryUpload;
